<template>
  <div class="undo-toast" role="status">
    <div class="undo-toast__content">
      <div class="undo-toast__text" :title="fullText">
        <span v-if="display.prefix">{{ display.prefix }}</span>

        <span class="undo-toast__tagText" v-if="display.tagPart">
          {{ display.tagPart }}
        </span>

        <span v-if="display.suffix">{{ display.suffix }}</span>
      </div>
    </div>

    <button class="undo-toast__action" @click="handleUndo" type="button">
      {{ actionText }}
    </button>
  </div>
</template>

<script>
  export default {
    name: "UndoToast",
    props: {
      message: {
        type: String,
        default: "",
      },
      actionText: {
        type: String,
        default: "Deshacer",
      },
      onUndo: {
        type: Function,
        default: null,
      },
      closeToast: {
        type: Function,
        default: null,
      },
      maxCharsMobile: {
        type: Number,
        default: 24,
      },
      mobileBreakpoint: {
        type: Number,
        default: 480,
      },
    },
    data() {
      return {
        isMobile: false,
        mediaQueryList: null,
        _mqListener: null,
      };
    },
    computed: {
      prefix() {
        return "Tag ";
      },
      suffixText() {
        return " eliminado.";
      },
      fullText() {
        return `${this.prefix}${this.message}${this.suffixText}`;
      },
      display() {
        if (!this.isMobile) {
          return {
            prefix: this.prefix,
            tagPart: this.message,
            suffix: this.suffixText,
          };
        }

        const max = Math.max(0, Number(this.maxCharsMobile) || 0);
        const full = this.fullText || "";

        if (full.length <= max) {
          return {
            prefix: this.prefix,
            tagPart: this.message,
            suffix: this.suffixText,
          };
        }

        const truncated = this._truncate(full, max);

        const tagStart = this.prefix.length;
        const tagEnd = tagStart + this.message.length;

        const dispPrefix = truncated.slice(0, Math.min(truncated.length, tagStart));
        const dispTag = truncated.slice(Math.max(0, Math.min(truncated.length, tagStart)), Math.min(truncated.length, tagEnd));
        const dispSuffix = truncated.slice(Math.max(0, tagEnd), truncated.length);

        return {
          prefix: dispPrefix || "",
          tagPart: dispTag || "",
          suffix: dispSuffix || "",
        };
      },
    },
    methods: {
      handleUndo() {
        if (typeof this.onUndo === "function") {
          try {
            this.onUndo();
          } catch (err) {
            console.error("Undo handler error", err);
          }
        }

        if (typeof this.closeToast === "function") {
          this.closeToast();
          return;
        }

        if (this.$toast && typeof this.$toast.dismiss === "function") {
          try {
            this.$toast.dismiss();
          } catch (e) {
            console.log("Error closing toast", e);
          }
        }
      },

      _truncate(str, max) {
        if (!str) return "";
        if (str.length <= max) return str;
        const cut = Math.max(0, max - 1);
        return str.slice(0, cut).trimEnd() + "…";
      },

      setupMediaQuery() {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
          this.isMobile = false;
          return;
        }

        const mq = `(max-width: ${this.mobileBreakpoint}px)`;
        this.mediaQueryList = window.matchMedia(mq);
        this.isMobile = !!this.mediaQueryList.matches;

        const listener = (e) => {
          this.isMobile = !!(e && e.matches);
        };

        if (typeof this.mediaQueryList.addEventListener === "function") {
          this.mediaQueryList.addEventListener("change", listener);
          this._mqListener = listener;
        } else if (typeof this.mediaQueryList.addListener === "function") {
          this.mediaQueryList.addListener(listener);
          this._mqListener = listener;
        } else {
          window.addEventListener("resize", this._updateIsMobileFallback);
        }
      },
      teardownMediaQuery() {
        if (!this.mediaQueryList) {
          window.removeEventListener("resize", this._updateIsMobileFallback);
          return;
        }
        if (this._mqListener) {
          if (typeof this.mediaQueryList.removeEventListener === "function") {
            this.mediaQueryList.removeEventListener("change", this._mqListener);
          } else if (typeof this.mediaQueryList.removeListener === "function") {
            this.mediaQueryList.removeListener(this._mqListener);
          }
        } else {
          window.removeEventListener("resize", this._updateIsMobileFallback);
        }
      },
      _updateIsMobileFallback() {
        this.isMobile = window.innerWidth <= this.mobileBreakpoint;
      },
    },
    mounted() {
      this.setupMediaQuery();
    },
    beforeDestroy() {
      this.teardownMediaQuery();
    },
    beforeUnmount() {
      this.teardownMediaQuery();
    },
  };
</script>

<style scoped lang="scss">
  .undo-toast {
    @include Flex(row, center, center);
    gap: 20px;
    padding: 8px 12px;

    &__content {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      min-width: 0;
    }

    &__text {
      font-size: 16px;
      line-height: 1.2;
      color: #3d3d3d;
      font-family: $newFont_regular;
      font-weight: 400;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }

    &__tagText {
      color: #3d3d3d;
      font-family: $newFont_medium;
      font-weight: 500;
      white-space: nowrap;
    }

    &__action {
      flex: 0 0 auto;
      background: transparent;
      border: none;
      color: $primary-color;
      font-family: $newFont_semiBold;
      font-weight: 500;
      cursor: pointer;
      padding: 6px 8px;
      border-radius: 10px;
      transition: all 0.3s ease-in-out;

      &:hover {
        background: rgba(220, 38, 38, 0.06);
      }

      white-space: nowrap;
      min-width: 48px;
    }
  }
</style>
